import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    mergeComponentCollections,
    ReactiveJsonRoot,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { KanbanBoard } from '../kanban/KanbanBoard';
import { TimeLogUserSelect, DatePickerField, TimeLogAddButton, TimeLogRow, TimeLogChangeNotifier, HoursMinutesField, HoursMinutesLabel } from './Timesheet';
import NewTaskModal from './NewTaskModal';
import { DataSyncSaveButton, SaveChannelButton, RichTextField, ComplexityDotsField, PriorityIconField, ConfirmTrashModal, FieldStatus, FieldStatusMessage, ImageLightbox, Modal, copyToClipboardReaction } from '../shared';
import { TaskTagsField } from './TaskTagsField';
// Rendu HTML en lecture seule : la conversation de l'assistant, capturée au
// signalement. Composant du builder, mais générique (il lit une chaîne, un
// {processed} ou un {value}) — cette racine est ISOLÉE, elle n'hérite d'aucun
// plugin de l'application, d'où l'import ici.
import { TaskTypeBadge } from './TaskTypeBadge';
import { RafComputedField } from './RafComputedField';
import { Archive, RotateCcw, Send, MessageSquare, X } from 'lucide-react';

import { FileField, FileFieldDelete, FileFieldPreview, FileFieldRetry } from '../file-field';
import { TaskStatusSelector } from './TaskStatusSelector';
import { TaskStateFresco } from './TaskStateFresco';
import { setPolicyContext } from '../kanban/kanbanTransitionPolicy';
import KanbanToast from '../kanban/KanbanToast';

import { saveComment, saveHistory, archiveTask, unarchiveTask, fetchCollection, deleteComment } from '../../services/api';
import { saveTask } from '../../services/api';
import { commentsProcessor, historyProcessor, tasksProcessor } from '../../dataProcessors/decoupledApiProcessors';
import { useMentionExtension } from '../shared/hooks/useMentionExtension';
import { JoinText } from '../builder/primitives';
import { MarkdownContent } from '../builder/MarkdownContent';
import { CornerDockSlot } from '../shell/CornerDock';
import { DOCK_ORDER } from '../shell/cornerDock';

const ALLOWED_TAGS = ['span', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'p', 'br', 'u', 's'];
const ALLOWED_ATTRS = { a: ['href', 'target', 'rel'], span: ['class', 'data-type', 'data-id', 'data-label'] };

function sanitizeCommentHtml(html) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent);
        if (node.nodeType !== Node.ELEMENT_NODE) return null;
        const tag = node.tagName.toLowerCase();
        if (!ALLOWED_TAGS.includes(tag)) {
            const frag = document.createDocumentFragment();
            for (const child of node.childNodes) { const c = walk(child); if (c) frag.appendChild(c); }
            return frag;
        }
        const el = document.createElement(tag);
        const allowed = ALLOWED_ATTRS[tag] || [];
        for (const attr of node.attributes) { if (allowed.includes(attr.name)) el.setAttribute(attr.name, attr.value); }
        for (const child of node.childNodes) { const c = walk(child); if (c) el.appendChild(c); }
        return el;
    };
    const frag = document.createDocumentFragment();
    for (const child of doc.body.childNodes) { const c = walk(child); if (c) frag.appendChild(c); }
    const tmp = document.createElement('div');
    tmp.appendChild(frag);
    return tmp.innerHTML;
}


/**
 * Build the dataOverride for the isolated ReactiveJsonRoot that renders TaskInfoPanel.yaml.
 *
 * task: flat task object from tasksProcessor (mapTaskFlat), e.g.:
 *   { id, title, status (label), type (TID string), rating, returnType (TID), ... _meta }
 *
 * Produces a Syncable Object at taskSyncable using the _meta.submissionUrl.
 * Status and type keep their label form (matching what tasksProcessor produces
 * and what Drupal decoupled_toolbox save accepts via kanban DataSync).
 */
function buildInfoPanelData({
    task,
    statusOptions,
    typeOptions,
    returnTypeOptions,
    evolutionTypeOptions,
    profileOptions,
    tagOptions,
    releaseOptions,
    budgetOptions,
    users,
    memberIds,
}) {
    // Workflow of the task's TYPE, resolved from the task_type collection options
    // (taskTypeCollectionProcessor exposes .data.workflow = { workflowStates,
    // workflowTransitions } per the decoupled_workflow_states formatter). null for
    // legacy types with no workflow → the selector then uses its taxonomy fallback.
    const typeOption = (typeOptions || []).find(o => String(o.value) === String(task?.type));
    const workflow = typeOption?.data?.workflow ?? null;
    // The focused task in POLICY shape ({ data: { id, type, state } }). Passed to
    // TaskStatusSelector, which delegates the allow-decision to the single policy
    // source (getAllowedTargets) — same gate the board / card menu use. The policy
    // reads parentId/children from its own seeded context (setPolicyContext in
    // TaskEdit), so the subtask guard works without threading children here.
    const policyTask = { data: { id: task?.id ?? null, type: task?.type ?? '', state: task?.state ?? null } };
    // taxonomyToOptionsProcessor already produces { value: TID, label: label }.
    // The task API response stores status/type as labels (after tasksProcessor maps them),
    // so we convert back to TIDs here so Drupal's save endpoint receives the expected bigint.
    const labelToTid = (options, label) =>
        (options || []).find(o => o.label === label)?.value ?? '';

    const statusTid = labelToTid(statusOptions, task?.status);
    const typeTid   = task?.type ?? '';

    // Drupal save endpoint expects the numeric user ID for assignee.
    // usersProcessor returns { id (numeric), name (username/display) }.
    // The task API returns assignee as a username string — convert it to ID.
    // Scope the candidate assignees to the project's MEMBERS (users with access).
    // Fallback to all users when the project has no members (so the field stays
    // usable), and always keep the currently-assigned user IN THE OPTIONS so a
    // valid (historical) assignment to a non-member still displays and stays
    // selectable instead of disappearing from the list.
    const assigneeUser = (users || []).find(u => u.name === (task?.assignee ?? ''));
    const ids = Array.isArray(memberIds) ? memberIds : [];
    let scopedUsers = ids.length ? (users || []).filter(u => ids.includes(Number(u.id))) : (users || []);
    if (assigneeUser && !scopedUsers.some(u => String(u.id) === String(assigneeUser.id))) {
        scopedUsers = [assigneeUser, ...scopedUsers];
    }
    const usersAsOptions = [
        { value: '', label: 'Non assigné' },
        ...scopedUsers.map(u => ({ value: String(u.id), label: u.name })),
    ];
    const assigneeId = assigneeUser ? String(assigneeUser.id) : '';

    const demandeEvolutionTid = (returnTypeOptions || []).find(o => o.label === "Demande d'évolution")?.value ?? '';

    const submissionUrl = task?._meta?.submissionUrl ?? null;

    // Workflow-driven tasks (a state AND a type workflow) persist `state` (the
    // string machine id the workflow selector writes); legacy taxonomy tasks
    // persist `status` (TID). The branch MUST match the YAML's hasWorkflow gate so
    // the visible selector and the syncable key always agree (a state without a
    // workflow object falls back to status, avoiding a dangling state write).
    const isWorkflowDriven = !!(task?.state && workflow);
    // L'IDENTITÉ (titre · type · tags · état) part dans le syncable de l'EN-TÊTE
    // persistant (TaskIdentityHeader.yaml) ; le RESTE des champs reste dans le
    // syncable de l'onglet Informations (TaskInfoPanel.yaml). Les deux roots ont
    // chacun leur DataSync ; le save decoupled étant PARTIEL (PATCH sur les seuls
    // champs envoyés — cf. DecoupledSubmissionController::save), ils ne s'écrasent
    // pas leurs champs respectifs.
    const headerSyncableData = {
        title: task?.title ?? '',
        type: typeTid,
        tags: task?.tags ?? [],
        ...(isWorkflowDriven ? { state: task.state } : { status: statusTid }),
    };
    const infoSyncableData = {
        description: task?.description ?? '',
        priority: task?.rating ?? 0,
        complexity: task?.complexity ?? 0,
        assignee: assigneeId,
        dueDate: task?.dueDate ?? '',
        estimation: task?.estimation ?? 0,
        returnType: task?.returnType || '',
        evolutionType: task?.evolutionType || '',
        release: task?.release || '',
        budget: task?.budget || '',
        internal: task?.internal ? '1' : '0',
        profile: task?.profile || '',
        // Technical context of a report, edited in its own block. The back end
        // renders it as an OBJECT; Orbit's submission controller re-encodes it
        // into the text field on save, so nothing special is needed here.
        // Carried even when untouched: the payload is sent whole.
        ...(task?.reportContext ? { reportContext: task.reportContext } : {}),
    };
    const taskSyncable = submissionUrl ? { submission_url: submissionUrl, data: infoSyncableData } : {};
    const taskSyncableHeader = submissionUrl ? { submission_url: submissionUrl, data: headerSyncableData } : {};

    // Options du badge TYPE de l'en-tête : « comme la création » — uniquement les
    // types de TÊTE (parentId 0), + TOUJOURS le type courant (même s'il est un
    // sous-type) pour que le badge l'affiche. Évite de réaffecter une tâche à un
    // sous-type par mégarde depuis la fiche. (L'exclusion des types INTERNES
    // demande l'exposition de field_type_internal en decoupled — suivi back.)
    const headerTypeOptions = (() => {
        const opts = typeOptions || [];
        const top = opts.filter((o) => !Number(o?.data?.parentId));
        if (task?.type != null && !top.some((o) => String(o.value) === String(task.type))) {
            const cur = opts.find((o) => String(o.value) === String(task.type));
            if (cur) return [cur, ...top];
        }
        return top;
    })();

    // Optional taxonomy selects (release, budget): prepend an empty option so the
    // field can be cleared / left blank. SelectField uses `dynamicOptions`
    // exclusively (it ignores the YAML static "" option), so the empty entry must
    // live in the options data itself.
    const prependEmpty = (opts) => {
        const list = Array.isArray(opts) ? opts : [];
        return list.some(o => String(o?.value ?? '') === '') ? list : [{ value: '', label: '—' }, ...list];
    };

    return {
        saveNow: false,
        taskSyncable,
        // Syncable disjoint de l'en-tête persistant (identité + état).
        saveNowHeader: false,
        taskSyncableHeader,
        // Une tâche archivée est en lecture seule côté serveur : tout POST échoue.
        // Les deux DataSync sont DÉMONTÉS via `hide` (l'action Hide rend null, donc
        // ils ne s'abonnent même pas au store) — sinon la simple arrivée sur la
        // fiche déclenchait une sauvegarde perdue d'avance.
        isArchived: !!(task?.archived || task?._meta?.archived),
        headerTypeOptions,
        statusOptions: statusOptions || [],   // TID-based: { value: TID, label: label }
        typeOptions:   typeOptions   || [],   // TID-based: { value: TID, label: label }
        returnTypeOptions: returnTypeOptions || [],
        evolutionTypeOptions: evolutionTypeOptions || [],
        profileOptions: profileOptions || [],
        tagOptions: tagOptions || [],
        releaseOptions: prependEmpty(releaseOptions),
        budgetOptions: prependEmpty(budgetOptions),
        usersAsOptions,
        demandeEvolutionTid,
        taskAuthor: task?.author ?? '',
        taskTimeSpent: task?.timeSpent ?? 0,
        taskRaf: task?.raf ?? 0,
        taskFiles: task?.attachedFiles ?? [],
        // URL de l'image ouverte dans la visionneuse (ImageLightbox) : '' = fermée.
        // Déclarée ici pour que la clé existe au premier rendu — la visionneuse
        // s'abonne au magasin sur ce chemin.
        lightboxUrl: '',
        taskId: task?.id ?? null,
        // Identifiant lisible (« ORB-14 », module orbit_ref), affiché et copiable
        // dans l'en-tête. '' tant que le projet n'a pas de clé (le back omet
        // alors la clé `ref`) : l'en-tête le masque.
        taskRef: task?.ref ?? '',
        // Statut de la copie de l'identifiant ('copied' | 'failed'), écrit par la
        // réaction copyToClipboard de l'en-tête.
        refCopyStatus: '',
        // Workflow state machine ID (field_task_state), null when not available.
        // Drives the YAML Switch that picks the workflow-bound (state) vs the
        // legacy taxonomy-bound (status) status selector.
        taskState: task?.state ?? null,
        // The type's workflow ({ workflowStates, workflowTransitions }) and the
        // task in policy shape — consumed by TaskStatusSelector's workflow mode.
        // null workflow → the selector falls back to legacy taxonomy options.
        taskWorkflow: workflow,
        policyTask,
        // Boolean gate for the YAML (is: true / is: false): true when this task is
        // workflow-driven (has a state AND its type exposes a workflow), false for
        // legacy taxonomy tasks. Drives which status selector variant is shown —
        // MUST equal isWorkflowDriven above so the gate and the syncable key agree.
        hasWorkflow: isWorkflowDriven,
    };
}

const TaskEdit = (componentProps) => {
    const navigate = useNavigate();
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);


    const props = (componentProps && componentProps.props) ? componentProps.props : componentProps;

    // ── Resolve task ──────────────────────────────────────────────────────────
    let attributes = evaluateTemplateValue({
        valueToEvaluate: props?.task,
        globalDataContext,
        templateContext,
    });

    if (!attributes || (typeof attributes === 'string' && attributes.startsWith('~'))) {
        attributes = globalDataContext?.data?.task || {};
    }
    if (Array.isArray(attributes)) attributes = attributes[0] || {};
    if (!attributes || typeof attributes !== 'object') attributes = {};

    // ── Resolve sub-tasks ─────────────────────────────────────────────────────
    const resolvedSubTasks = evaluateTemplateValue({
        valueToEvaluate: props.subTasks,
        globalDataContext,
        templateContext,
    }) || attributes.subTasks || attributes.tasks || [];

    const [subTasks, setSubTasks] = useState(resolvedSubTasks);

    // ── Tabs ──────────────────────────────────────────────────────────────────
    const [activeRightTab, setActiveRightTab] = useState('message');
    // Activity (comments/history) panel: static right pane at lg+, slide-in overlay below lg.
    const [showActivity, setShowActivity] = useState(false);
    // Close the mobile/tablet activity overlay on Escape (FAB is lg:hidden, so
    // showActivity is only ever true below lg).
    useEffect(() => {
        if (!showActivity) return;
        const onKey = (e) => { if (e.key === 'Escape') setShowActivity(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [showActivity]);
    const [activeMainTab, setActiveMainTab] = useState(
        (resolvedSubTasks && resolvedSubTasks.length > 0) ? 'subtasks' : 'infos'
    );
    const [hasAutoSwitchedTab, setHasAutoSwitchedTab] = useState(
        resolvedSubTasks && resolvedSubTasks.length > 0
    );

    useEffect(() => {
        if (!hasAutoSwitchedTab && subTasks && subTasks.length > 0) {
            setActiveMainTab('subtasks');
            setHasAutoSwitchedTab(true);
        }
    }, [subTasks, hasAutoSwitchedTab]);

    // ── Users & options ───────────────────────────────────────────────────────
    const users = evaluateTemplateValue({
        valueToEvaluate: props.users,
        globalDataContext,
        templateContext,
    }) || props.data?.users || [];

    const currentUserRole = evaluateTemplateValue({
        valueToEvaluate: props.currentUserRole,
        globalDataContext,
        templateContext,
    }) || 'Utilisateur';

    const returnTypeOptions = evaluateTemplateValue({
        valueToEvaluate: props.returnTypeOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const evolutionTypeOptions = evaluateTemplateValue({
        valueToEvaluate: props.evolutionTypeOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const profileOptions = evaluateTemplateValue({
        valueToEvaluate: props.profileOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const rawStatusOptions = evaluateTemplateValue({
        valueToEvaluate: props.statusOptions,
        globalDataContext,
        templateContext,
    }) || [];
    const statusOptions = Array.isArray(rawStatusOptions) ? rawStatusOptions : [];

    const rawTypeOptions = evaluateTemplateValue({
        valueToEvaluate: props.typeOptions,
        globalDataContext,
        templateContext,
    }) || [];
    const typeOptions = Array.isArray(rawTypeOptions) ? rawTypeOptions : [];

    const tagOptions = evaluateTemplateValue({
        valueToEvaluate: props.tagOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const releaseOptions = evaluateTemplateValue({
        valueToEvaluate: props.releaseOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const budgetOptions = evaluateTemplateValue({
        valueToEvaluate: props.budgetOptions,
        globalDataContext,
        templateContext,
    }) || [];

    const findOptionValue = (options, label) => {
        if (!label || !Array.isArray(options)) return '';
        const opt = options.find(o => o.label === label);
        return opt ? opt.value : '';
    };

    const demandeEvolutionTid = findOptionValue(returnTypeOptions, "Demande d'évolution");

    // ── Sub-task modal ────────────────────────────────────────────────────────
    const [isSubTaskModalOpen, setIsSubTaskModalOpen] = useState(false);
    const [subTaskType, setSubTaskType] = useState('Sub-task');
    const [hoveredForbidden, setHoveredForbidden] = useState(null);

    // ── Archive confirmation ─────────────────────────────────────────────────
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);

    // ── Project context ───────────────────────────────────────────────────────
    // The page does not fetch the project resource up-front, so the human-readable
    // NAME is resolved lazily (effect below) into LOCAL state — that guarantees a
    // re-render (TaskEdit's View is not subscribed to the ~.project/~.pageTitle
    // store paths, so updateData alone would not refresh it). attributes.projectId
    // is available immediately (blocking task source) and is the authoritative id
    // fallback so the back link / redirects / time-log projectId work right away.
    const [resolvedProject, setResolvedProject] = useState(null);
    const lastFetchedProjectId = useRef(null);
    // CDC-TMA recette 3: the current user's cumulative group_role machine ids in
    // THIS project (GET /api/project/{id}/role -> { role, machine, roles: [] }).
    // Feeds the permission pre-filter in kanbanTransitionPolicy so the fiche status
    // selector offers exactly the transitions the user may perform. Empty until the
    // role effect returns; the policy tolerates that (it never masks on a missing
    // contract) so the switch degrades to reachable ∩ subtasks while it loads.
    const [currentUserRoles, setCurrentUserRoles] = useState([]);
    const lastFetchedRoleProjectId = useRef(null);
    const projectFromContext = globalDataContext?.data?.project;
    const projectId = resolvedProject?.id ?? projectFromContext?.id ?? attributes.projectId ?? null;
    const projectName = resolvedProject?.name || projectFromContext?.name || globalDataContext?.data?.pageTitle || '';
    const project = { id: projectId, name: projectName };
    // Users with access to the project (members), used to scope the assignee
    // field. Empty until the project resolve effect returns; consumers fall back
    // to all users when empty so the field is never unusable.
    const projectMemberIds = resolvedProject?.memberIds ?? projectFromContext?.memberIds ?? [];
    // Members-only user list for the subtask creation modal (falls back to all
    // users when the project has no members so the field stays usable).
    const memberUsers = (Array.isArray(users) && projectMemberIds.length)
        ? users.filter(u => projectMemberIds.includes(Number(u.id)))
        : (Array.isArray(users) ? users : []);

    useEffect(() => {
        const pid = attributes.projectId;
        if (!pid || lastFetchedProjectId.current === pid) return;
        lastFetchedProjectId.current = pid;
        let cancelled = false;
        (async () => {
            try {
                const raw = await fetchCollection(`/decoupled-api/group/project/${pid}?display=decoupled&_=${Date.now()}`, true);
                const d = raw?.data || {};
                const name = d.name || '';
                if (cancelled) return;
                // memberIds = users with access to the project; used to scope the
                // assignee field to project members.
                const memberIds = (Array.isArray(d.memberIds) ? d.memberIds : []).map(Number).filter(Number.isFinite);
                setResolvedProject({ id: Number(d.id) || pid, name, memberIds });
                // Mirror into the global store for any other consumer; local state
                // above is the authoritative refresh mechanism.
                if (globalDataContext.updateData && name) {
                    globalDataContext.updateData(name, 'pageTitle');
                    globalDataContext.updateData(Number(d.id) || pid, 'project.id');
                    globalDataContext.updateData(name, 'project.name');
                }
            } catch (e) {
                // 403 / failure → leave resolvedProject null; the back link degrades
                // to 'Retour aux projets' and uses attributes.projectId for the id.
                console.error('project resolve failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [attributes.projectId]);

    // ── Project role (for the workflow transition policy) ─────────────────────
    // Lazily fetch the user's cumulative group_role machine ids in this project,
    // mirroring the project-resolve effect above. On 403/failure the roles stay
    // empty and the policy tolerates the missing contract (no masking).
    useEffect(() => {
        const pid = attributes.projectId;
        if (!pid || lastFetchedRoleProjectId.current === pid) return;
        lastFetchedRoleProjectId.current = pid;
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetch(`/api/project/${pid}/role`, { credentials: 'include' });
                if (!resp.ok) return;
                const json = await resp.json();
                if (cancelled) return;
                const roles = Array.isArray(json?.roles) ? json.roles.map(String) : [];
                setCurrentUserRoles(roles);
                // Parité recette 3 : expose the full role payload to the subroot
                // GlobalDataContext (same shape as kanban2.yaml's /api/project/{id}/role
                // datasource) so the sub-task board's cards/list resolve the role like
                // the main kanban — KanbanSortableWrapper reads gdc.data.currentUserRoleData
                // (Désarchiver) and KanbanBoard falls back to it for the policy seed.
                if (globalDataContext?.updateData) {
                    globalDataContext.updateData(json, 'currentUserRoleData');
                }
            } catch (e) {
                console.error('project role resolve failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [attributes.projectId]);

    // ── Comments ──────────────────────────────────────────────────────────────
    const [comments, setComments] = useState(attributes.comments || []);
    const [editingCommentId, setEditingCommentId] = useState(null);

    const mentionExt = useMentionExtension();
    // Holds the latest handleAddComment so the editor key handler (created once)
    // never calls a stale closure.
    const submitCommentRef = useRef(null);
    const commentEditor = useEditor({
        extensions: [
            StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false, blockquote: false }),
            Placeholder.configure({ placeholder: 'Ajouter un commentaire… (@ pour mentionner · Maj+Entrée pour envoyer)' }),
            mentionExt,
        ],
        content: '',
        editorProps: {
            handleKeyDown(view, event) {
                // Shift+Enter submits — but never while the @-mention popup is open
                // (its Enter/Tab selects a mention). Plain Enter stays a newline.
                if (event.key === 'Enter' && event.shiftKey) {
                    if (document.querySelector('.mention-popup')) return false;
                    event.preventDefault();
                    submitCommentRef.current?.();
                    return true;
                }
                return false;
            },
        },
    });

    useEffect(() => {
        return () => { if (commentEditor) commentEditor.destroy(); };
    }, [commentEditor]);

    // Dedicated editor for in-place comment EDITING (one comment edited at a
    // time). Loaded with the comment's rich HTML so @mention spans survive.
    const editMentionExt = useMentionExtension();
    const editCommentEditor = useEditor({
        extensions: [
            StarterKit.configure({ heading: false, codeBlock: false, horizontalRule: false, blockquote: false }),
            Placeholder.configure({ placeholder: 'Modifier le commentaire…' }),
            editMentionExt,
        ],
        content: '',
    });

    useEffect(() => {
        return () => { if (editCommentEditor) editCommentEditor.destroy(); };
    }, [editCommentEditor]);

    // ── History ───────────────────────────────────────────────────────────────
    const [history, setHistory] = useState(attributes.history || []);

    // ── Refresh after save ────────────────────────────────────────────────────
    const refreshComments = useCallback(async () => {
        if (!attributes.id) return;
        const url = `/decoupled-api/comment/task_comment/collection?display=decoupled&filter[0][f]=entity_id&filter[0][v]=${attributes.id}&_=${Date.now()}`;
        try {
            const raw = await fetchCollection(url, true);
            const processed = commentsProcessor({ requestContext: { url }, responseContext: { status: 200 }, dataToProcess: raw });
            setComments(processed);
        } catch (e) { console.error('refreshComments failed', e); }
    }, [attributes.id]);

    const refreshHistory = useCallback(async () => {
        if (!attributes.id) return;
        const url = `/decoupled-api/comment/task_history/collection?display=decoupled&filter[0][f]=entity_id&filter[0][v]=${attributes.id}&_=${Date.now()}`;
        try {
            const raw = await fetchCollection(url, true);
            const processed = historyProcessor({ requestContext: { url }, responseContext: { status: 200 }, dataToProcess: raw });
            setHistory(processed);
        } catch (e) { console.error('refreshHistory failed', e); }
    }, [attributes.id]);

    useEffect(() => {
        const handleTaskSaved = () => {
            refreshHistory();
            refreshComments();
        };
        window.addEventListener('task-saved', handleTaskSaved);
        return () => window.removeEventListener('task-saved', handleTaskSaved);
    }, [refreshHistory, refreshComments]);

    // ── Sync sub-data from global context ─────────────────────────────────────
    useEffect(() => {
        if (!attributes) return;

        if (attributes.comments) {
            setComments(prev => {
                const next = attributes.comments;
                return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
            });
        }

        const incomingSubTasks = evaluateTemplateValue({
            valueToEvaluate: props.subTasks,
            globalDataContext,
            templateContext,
        }) || attributes.subTasks || attributes.tasks;

        if (incomingSubTasks) {
            setSubTasks(prev => {
                const next = incomingSubTasks;
                return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
            });
        }

        if (attributes.history) {
            setHistory(prev => {
                const next = attributes.history;
                return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
            });
        }
    }, [
        JSON.stringify(attributes?.comments),
        JSON.stringify(attributes?.history),
        JSON.stringify(evaluateTemplateValue({ valueToEvaluate: props.subTasks, globalDataContext, templateContext })),
    ]);

    // ── Time log (ReactiveJsonRoot) ───────────────────────────────────────────
    const todayStr = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();

    const timeLogCollectionUrl = attributes.id
        ? `/decoupled-api/node/time_log/collection?display=decoupled&filter[0][f]=field_task&filter[0][v]=${attributes.id}`
        : null;

    // userInfo comes from the blocking /api/user/info source (~~.userInfo). The
    // GlobalDataContext exposes no `.data` member, so it must be read via the
    // template system (evaluateTemplateValue), not globalDataContext.data.
    const userInfo = evaluateTemplateValue({ valueToEvaluate: '~~.userInfo', globalDataContext, templateContext }) || {};
    const currentUserId = userInfo.uid;

    const timeLogData = useMemo(() => {
        if (!attributes.id) return undefined;

        const userOpts = (Array.isArray(users) ? users : [])
            .filter(u => u.id && u.name)
            .map(u => ({ value: String(u.id), label: u.name }));

        const newTL = {
            submission_url: '/decoupled-api/save?display=decoupled&entity_type=node&bundle=time_log',
            data: {
                title: 'Time log',
                date: todayStr,
                user: currentUserId ? String(currentUserId) : '',
                description: '',
                timeSpent: '',
                timeLeft: '',
                taskId: String(attributes.id),
                projectId: String(project.id || ''),
            },
            status: null,
        };

        return {
            timeLogs: [],
            userOptions: userOpts,
            newTimeLog: { ...newTL },
            newTimeLogTemplate: { ...newTL },
            saveTimeLogNow: false,
            taskId: String(attributes.id),
            projectId: String(project.id || ''),
            timeLogCollectionUrl,
        };
    }, [attributes.id, project.id, users, timeLogCollectionUrl, todayStr, currentUserId]);

    const timeLogPlugins = useMemo(() => mergeComponentCollections([{
        element: { TimeLogUserSelect, DatePickerField, TimeLogAddButton, TimeLogRow, TimeLogChangeNotifier, HoursMinutesField, FieldStatus, FieldStatusMessage },
    }]), []);

    // After a time log is created/edited/deleted, the server recomputes the task's
    // field_time_spent (SUM) and field_raf (latest log). Refetch just those two
    // numbers and push them into the store so the open fiche's Charge & temps
    // display updates live (no full reload).
    const refetchTaskTimeFields = useCallback(async () => {
        if (!attributes.id) return;
        const base = attributes._meta?.itemUrl || `/decoupled-api/node/task/${attributes.id}?display=decoupled`;
        const url = base + (base.includes('?') ? '&' : '?') + `_=${Date.now()}`;
        try {
            const raw = await fetchCollection(url, true);
            const d = raw?.data || {};
            // decoupled_field_hide_empty omits a 0 value → coerce missing keys to 0.
            const ts = Number(d.timeSpent) || 0;
            const rf = Number(d.raf) || 0;
            if (globalDataContext.updateData) {
                globalDataContext.updateData(ts, 'task.timeSpent');
                globalDataContext.updateData(rf, 'task.raf');
            }
        } catch (e) { console.error('refetch task time fields failed', e); }
    }, [attributes.id, attributes._meta?.itemUrl]);

    useEffect(() => {
        const onUpdated = (e) => {
            if (e?.detail?.id && String(e.detail.id) !== String(attributes.id)) return;
            refetchTaskTimeFields();
        };
        window.addEventListener('task:entity-updated', onUpdated);
        return () => window.removeEventListener('task:entity-updated', onUpdated);
    }, [attributes.id, refetchTaskTimeFields]);

    // ── Transition policy seed (fiche status selector) ────────────────────────
    // Seed the SAME module-level policy the Kanban board seeds (setPolicyContext),
    // so TaskStatusSelector's workflow mode resolves reachable ∩ role-permitted ∩
    // subtasks-OK targets identically to the board / card menu — one source of
    // decision, no duplicated role logic. taskTypes = the task_type options (they
    // carry .data.workflow via taskTypeCollectionProcessor); allTasks = this task
    // plus its loaded subtasks (the policy reads parentId/state via its own dataOf
    // unwrap to evaluate the subtask-closure guard). Re-runs on type/role/subtask
    // changes. Mirrors KanbanBoard's seeding effect.
    useEffect(() => {
        const allTasks = [attributes, ...(Array.isArray(subTasks) ? subTasks : [])];
        setPolicyContext({ taskTypes: typeOptions, userRoles: currentUserRoles, allTasks });
    }, [
        JSON.stringify(typeOptions),
        JSON.stringify(currentUserRoles),
        attributes?.id,
        attributes?.state,
        JSON.stringify(subTasks),
    ]);

    // ── Info panel plugins ────────────────────────────────────────────────────
    const infoPanelData = useMemo(() => buildInfoPanelData({
        task: attributes,
        statusOptions,
        typeOptions,
        returnTypeOptions,
        evolutionTypeOptions,
        profileOptions,
        tagOptions,
        releaseOptions,
        budgetOptions,
        users,
        memberIds: projectMemberIds,
    }), [
        attributes?.id,
        attributes?.type,
        attributes?.state,
        attributes?.ref,
        attributes?.timeSpent,
        attributes?.raf,
        JSON.stringify(attributes?.attachedFiles),
        JSON.stringify(statusOptions),
        JSON.stringify(typeOptions),
        JSON.stringify(returnTypeOptions),
        JSON.stringify(evolutionTypeOptions),
        JSON.stringify(profileOptions),
        JSON.stringify(tagOptions),
        JSON.stringify(releaseOptions),
        JSON.stringify(budgetOptions),
        JSON.stringify(users),
        JSON.stringify(projectMemberIds),
    ]);

    // Collection LOCALE : le panneau est monté en subroot, il ne voit donc que ce
    // qui est déclaré ici — pas les plugins de l'application. `Modal` et
    // `MarkdownContent` servent la prévisualisation des pièces jointes ; sans
    // eux reactive-json rendrait des balises <modal> inertes.
    const infoPanelPlugins = useMemo(() => mergeComponentCollections([{
        element: { DataSyncSaveButton, FileField, FileFieldDelete, FileFieldPreview, FileFieldRetry, ImageLightbox, RichTextField, ComplexityDotsField, PriorityIconField, TaskStatusSelector, TaskTagsField, TaskTypeBadge, RafComputedField, TimeLogUserSelect, HoursMinutesField, HoursMinutesLabel, FieldStatus, FieldStatusMessage, JoinText, Modal, MarkdownContent },
    }]), []);

    // Plugins de l'EN-TÊTE persistant (identité éditable + fresque d'état).
    // Racine isolée : la réaction de copie de l'identifiant (ORB-14) doit être
    // déclarée ici, celles de l'application n'y sont pas visibles.
    const headerPlugins = useMemo(() => mergeComponentCollections([{
        element: { TaskTypeBadge, TaskTagsField, TaskStateFresco, TaskStatusSelector, DataSyncSaveButton, FieldStatus, FieldStatusMessage },
        reaction: { copyToClipboard: copyToClipboardReaction },
    }]), []);

    // Agent IA du ticket (orbit_agent_relay) : bouton + popin tout en YAML
    // (TaskAgentActions.yaml). Seules les URLs, qui portent l'id du ticket, sont
    // composées ici — l'`url` d'un submitData/fetchData n'évalue qu'une valeur,
    // sans segments. Jeu de données COMPLET : dataOverride remplace le `data:`
    // du YAML.
    const agentRelayData = useMemo(() => {
        const base = `/api/orbit/agent-relay/task/${attributes?.id ?? ''}`;
        return {
            statusUrl: `${base}/status`,
            requestUrl: `${base}/request`,
            cancelUrl: `${base}/cancel`,
            showModal: false,
            draft: { plan: true, instruction: '' },
            relay: { available: false, canPlan: false, canWrite: false, busy: false, locked: false, cancellable: false, statusLabel: '', detailPlain: '', detailError: '' },
        };
    }, [attributes?.id]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const formatHistoryDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const now = new Date();
            const diffMs = now - d;
            const diffMin = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            if (diffMin < 1) return "À l'instant";
            if (diffMin < 60) return `Il y a ${diffMin} min`;
            if (diffHours < 24) return `Il y a ${diffHours}h`;
            if (diffDays < 7) return `Il y a ${diffDays}j`;
            return d.toLocaleDateString('fr-FR', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return dateStr;
        }
    };

    // Real current user (display name) from the blocking /api/user/info source.
    // Used both for the optimistic comment author and the edit-permission gate.
    const currentUserName = userInfo.name || '';
    const isAdmin = (userInfo.roles || []).includes('administrator');

    // A comment can be edited/deleted by an ADMINISTRATOR or by its CREATOR.
    // The creator is matched by display name, with a username->uid fallback via
    // the loaded users list so it still holds if the display name differs.
    const canManageComment = (comment) => {
        if (isAdmin) return true;
        if (!comment?.author) return false;
        if (currentUserName && comment.author === currentUserName) return true;
        const authorId = Array.isArray(users) ? users.find((u) => u?.name === comment.author)?.id : undefined;
        return authorId != null && String(authorId) === String(currentUserId);
    };

    const handleAddComment = async () => {
        if (!commentEditor) return;
        const html = commentEditor.getHTML();
        if (!html || html === '<p></p>') return;

        const plainText = commentEditor.getText();
        commentEditor.commands.clearContent();

        const optimistic = { id: Date.now(), author: currentUserName, date: "À l'instant", text: plainText };
        setComments(prev => [optimistic, ...prev]);

        if (attributes.id) {
            try {
                await saveComment({
                    text: html,
                    subject: plainText.substring(0, 60),
                    entityId: String(attributes.id),
                    commentedEntityType: 'node',
                    fieldName: 'field_comments',
                });
                const url = `/decoupled-api/comment/task_comment/collection?display=decoupled&filter[0][f]=entity_id&filter[0][v]=${attributes.id}`;
                const raw = await fetchCollection(url, true);
                const processed = commentsProcessor({
                    requestContext: { url },
                    responseContext: { status: 200 },
                    dataToProcess: raw,
                });
                setComments(processed);
                if (globalDataContext.updateData) {
                    globalDataContext.updateData(processed, 'task.comments');
                }
            } catch (error) {
                console.error('Error saving comment:', error);
            }
        }
    };
    // Keep the editor key handler bound to the latest handleAddComment (no stale closure).
    submitCommentRef.current = handleAddComment;

    const handleEditComment = (comment, idx) => {
        setEditingCommentId(comment.id || idx);
        // Load the rich HTML so @mention spans survive the round-trip.
        if (editCommentEditor) editCommentEditor.commands.setContent(comment.rawHtml || comment.text || '');
    };

    const handleSaveComment = async (idx) => {
        const comment = comments[idx];
        if (!comment || !attributes.id || !editCommentEditor || isArchived) return;
        const html = editCommentEditor.getHTML();
        const plain = editCommentEditor.getText().trim();
        if (!html || html === '<p></p>') return;
        try {
            await saveComment({
                id: comment.id,
                text: html,
                subject: plain.substring(0, 60),
                entityId: String(attributes.id),
                commentedEntityType: 'node',
                fieldName: 'field_comments',
            });
            // Refetch so the persisted (sanitized) comment replaces the local copy.
            const url = `/decoupled-api/comment/task_comment/collection?display=decoupled&filter[0][f]=entity_id&filter[0][v]=${attributes.id}&_=${Date.now()}`;
            const raw = await fetchCollection(url, true);
            const processed = commentsProcessor({ requestContext: { url }, responseContext: { status: 200 }, dataToProcess: raw });
            setComments(processed);
            if (globalDataContext.updateData) globalDataContext.updateData(processed, 'task.comments');
        } catch (error) {
            console.error('Error updating comment:', error);
        } finally {
            setEditingCommentId(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
    };

    const handleDeleteComment = async (comment) => {
        if (!comment?.id || !attributes.id || isArchived) return;
        if (!window.confirm('Supprimer ce commentaire ?')) return;
        try {
            await deleteComment(comment.id);
            const url = `/decoupled-api/comment/task_comment/collection?display=decoupled&filter[0][f]=entity_id&filter[0][v]=${attributes.id}&_=${Date.now()}`;
            const raw = await fetchCollection(url, true);
            const processed = commentsProcessor({ requestContext: { url }, responseContext: { status: 200 }, dataToProcess: raw });
            setComments(processed);
            if (globalDataContext.updateData) globalDataContext.updateData(processed, 'task.comments');
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const handleCreateSubTask = () => {
        const subtaskTypeOptions = typeOptions.filter(o => String(o.data?.parentId) === String(attributes.type));
        const firstSubtypeTid = subtaskTypeOptions[0]?.value ?? '';
        setSubTaskType(firstSubtypeTid);
        setIsSubTaskModalOpen(true);
    };

    const refreshSubTasks = async () => {
        if (!attributes.id) return;
        const url = `/decoupled-api/node/task/collection?display=decoupled&filter[0][f]=field_parent_task&filter[0][v]=${attributes.id}&_=${Date.now()}`;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) return;
        const rawData = await resp.json();
        const processed = tasksProcessor({
            requestContext: { url },
            responseContext: { status: 200 },
            dataToProcess: rawData,
        });
        if (Array.isArray(processed)) {
            setSubTasks(processed);
            if (globalDataContext.updateData) {
                globalDataContext.updateData(processed, 'tasks');
            }
        }
    };

    const handleSaveSubTask = async (taskData) => {
        let assigneeId = null;
        if (taskData.assignee) {
            // Resolve against members only, so a stale free-typed name cannot assign a non-member.
            const user = memberUsers.find(u => u.name === taskData.assignee);
            assigneeId = user ? user.id : null;
        }

        try {
            await saveTask({
                title: taskData.title,
                type: taskData.type || subTaskType,
                parent_task: attributes.id,
                priority: taskData.rating,
                status: taskData.status,
                assigned_to: assigneeId,
                complexity: taskData.complexity,
                tags: taskData.tags,
                returnType: taskData.returnType,
                evolutionType: taskData.evolutionType,
                description: taskData.description,
                attachedFiles: taskData.attachedFiles,
                dueDate: taskData.dueDate,
                projectId: project.id,
                _statusOptions: statusOptions,
                _typeOptions: typeOptions,
                _returnTypeOptions: returnTypeOptions,
                _evolutionTypeOptions: evolutionTypeOptions,
                _profileOptions: profileOptions,
            });
            await refreshSubTasks();
        } catch (error) {
            console.error('Error saving subtask:', error);
        }
    };

    const handleArchiveTask = async () => {
        if (!attributes.id || archiveLoading) return;
        setArchiveLoading(true);
        try {
            await archiveTask(attributes.id);
            window.dispatchEvent(new CustomEvent('task:entity-archived', {
                detail: { type: 'task', id: attributes.id, projectId: project.id || null },
            }));
            const target = project.id ? `/kanban2?id=${project.id}` : '/projets';
            navigate(target);
        } catch (error) {
            console.error('Error archiving task:', error);
            const msg = error.message?.includes('403')
                ? 'Permission refusée. Vous n\'avez pas les droits pour archiver cette tâche.'
                : `Erreur : ${error.message}`;
            alert(msg);
        } finally {
            setArchiveLoading(false);
            setShowArchiveConfirm(false);
        }
    };

    const handleUnarchiveTask = async () => {
        if (!attributes.id || archiveLoading) return;
        setArchiveLoading(true);
        try {
            await unarchiveTask(attributes.id);
            // La fiche est lue au montage : rien de moins qu'un rechargement ne
            // remet d'aplomb l'en-tête, les onglets ET les DataSync démontés par
            // l'état archivé. C'est une action rare et délibérée.
            window.location.reload();
        } catch (error) {
            console.error('Error unarchiving task:', error);
            const msg = error.message?.includes('403')
                ? 'Permission refusée. Seuls les administrateurs et chefs de projet peuvent désarchiver.'
                : `Erreur : ${error.message}`;
            alert(msg);
            setArchiveLoading(false);
        }
    };

    // ── Archive read-only detection ──────────────────────────────────────────
    const isArchived = !!(attributes.archived || attributes._meta?.archived);
    // Qui peut désarchiver : la même règle que le serveur (OrbitArchiveManager::
    // canUnarchive) et que les cartes du kanban — la permission globale, ou un
    // rôle de pilotage sur le projet de la tâche. Cacher le bouton plutôt que de
    // laisser un 403 pour seule réponse.
    const canUnarchive = isAdmin
        || (userInfo.permissions || []).includes('unarchive content')
        || currentUserRoles.some(r => ['project-admin', 'project-cdp'].includes(String(r)));
    // Read-only treatment for the TAB PANELS only — never the tab bar. Applied to
    // the whole form card, it also froze the tabs themselves: an archived task
    // opened on its default tab and could not be navigated, so half its content
    // was unreachable.
    const archivedPanelClass = isArchived ? 'pointer-events-none opacity-60' : '';

    // ── Derived ───────────────────────────────────────────────────────────────
    const subtaskTypeOptions = typeOptions.filter(o => String(o.data?.parentId) === String(attributes?.type));

    // (L'ancien récap d'identité lecture seule a été remplacé par l'en-tête
    // PERSISTANT éditable — TaskIdentityHeader.yaml, monté plus bas.)

    // ── Styles ────────────────────────────────────────────────────────────────
    const rightTabClass = (isActive) => isActive
        ? 'bg-white text-iris-700 font-semibold border-b-2 border-iris-600 flex-1 py-3 text-center text-sm'
        : 'bg-white text-slate-500 hover:text-slate-700 font-medium flex-1 py-3 text-center text-sm border-b border-slate-200';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <ActionDependant {...props}>
            <>
                {/* Toast host for transition-refusal feedback on the task sheet
                    (point 1 parity with the board), on the kanbanNotify bus. The
                    fiche is a standalone route, so this never double-mounts with
                    the board's own KanbanToast. */}
                <KanbanToast />
                <NewTaskModal
                    isOpen={isSubTaskModalOpen}
                    onClose={() => setIsSubTaskModalOpen(false)}
                    onSave={handleSaveSubTask}
                    taskType={subTaskType}
                    isSubTask={true}
                    users={memberUsers}
                    returnTypeOptions={returnTypeOptions}
                    evolutionTypeOptions={evolutionTypeOptions}
                    tagOptions={tagOptions}
                    typeOptions={subtaskTypeOptions}
                />
                <div className="flex-grow flex flex-col font-sans text-sm h-full min-h-0 relative">
                    {hoveredForbidden && (
                        <div
                            style={{
                                position: 'fixed',
                                top: hoveredForbidden.top,
                                left: hoveredForbidden.left,
                                transform: 'translateX(-50%)',
                                zIndex: 9999,
                                width: 'max-content',
                                pointerEvents: 'none',
                            }}
                            className="bg-slate-800 text-white text-xs px-3 py-2 rounded shadow-lg max-w-[200px] text-center"
                        >
                            {hoveredForbidden.reason}
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45"></div>
                        </div>
                    )}

                    <div className="flex flex-col lg:flex-row flex-grow h-full min-h-0 relative">
                        {/* Main Content */}
                        <div className="flex-grow min-w-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-y-auto">
                          <div className="w-full">

                            {/* Persistent task header — rendered outside the tab
                                conditionals so the ticket context (project link, type,
                                title, tags, archive/trash) stays visible on every tab.
                                Identity (row 2) is READ-ONLY: editing type/title/tags +
                                Save live in the Informations tab form (single DataSync). */}
                            <div className="mb-4 space-y-3">
                                {/* Row 1: back-to-project + archive/trash */}
                                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                                    <a
                                        href={attributes.parentId
                                            ? `/task-edit?id=${attributes.parentId}`
                                            : (project.id ? `/kanban2?id=${project.id}` : '/projets')}
                                        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-iris-600 transition-colors font-medium group min-w-0"
                                    >
                                        <div className="p-1 rounded-full bg-slate-100 group-hover:bg-iris-100 transition-colors shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M19 12H5M12 19l-7-7 7-7" />
                                            </svg>
                                        </div>
                                        <span className="truncate">
                                            {attributes.parentId
                                                ? 'Retour à la tâche parente'
                                                : (project.name ? `Retour au projet ${project.name}` : 'Retour aux projets')}
                                        </span>
                                    </a>

                                    {/* Contenu lié — la fiche à laquelle la tâche se rapporte
                                        (entreprise, transaction, contact…). Posé à côté du retour
                                        au projet : c'est du CONTEXTE de navigation, pas un champ du
                                        formulaire — d'où le rendu ici, dans cette barre JSX, et non
                                        dans une des racines reactive-json de la fiche (elles
                                        commencent sous cette ligne). Même puce que la carte kanban :
                                        type à gauche, titre entier à droite. */}
                                    {(attributes.linkedContentRefs ?? []).map((ref) => (
                                        <div key={ref.id} className="flex items-center gap-2 min-w-0">
                                            <span className="badge badge-neutral badge-sm shrink-0">{ref.typeLabel}</span>
                                            {ref.url ? (
                                                <a
                                                    href={ref.url}
                                                    className="text-sm font-medium text-iris-600 hover:underline break-words"
                                                >
                                                    {ref.title}
                                                </a>
                                            ) : (
                                                /* Bundle sans écran dédié dans le SPA : pas de lien mort. */
                                                <span className="text-sm font-medium text-slate-700 break-words">{ref.title}</span>
                                            )}
                                        </div>
                                    ))}

                                    <div className="flex items-center gap-1 ml-auto shrink-0">
                                        {isArchived && (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded-lg">
                                                <Archive size={14} /> Archivé — lecture seule
                                            </span>
                                        )}

                                        {isArchived && canUnarchive && (
                                            <button
                                                onClick={handleUnarchiveTask}
                                                disabled={archiveLoading}
                                                className="btn btn-secondary btn-sm"
                                                title="Désarchiver la tâche"
                                            >
                                                <RotateCcw size={14} /> Désarchiver
                                            </button>
                                        )}

                                        {attributes.id && !isArchived && (
                                            <>
                                                {/* Agent IA du ticket : bouton + popin (YAML). */}
                                                <ReactiveJsonRoot
                                                    key={`agent-${attributes.id}`}
                                                    rjBuildUrl="/components/task/TaskAgentActions.yaml"
                                                    dataOverride={agentRelayData}
                                                    plugins={infoPanelPlugins}
                                                />
                                                {/* Bouton de sauvegarde UNIQUE de la fiche : les deux
                                                    racines reactive-json (en-tête persistant + onglet
                                                    Informations) gardent chacune leur DataSync et leur
                                                    syncable disjoint, mais n'exposent plus de bouton —
                                                    elles s'inscrivent au canal `task` (membres headless)
                                                    que ce bouton agrège et déclenche. */}
                                                <SaveChannelButton channel="task" className="mr-1" />
                                                <button
                                                    onClick={() => setShowArchiveConfirm(true)}
                                                    className="text-slate-400 hover:text-blue-600 transition-colors p-2 rounded-md hover:bg-blue-50"
                                                    title="Archiver la tâche"
                                                >
                                                    <Archive size={18} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Row 2: persistent EDITABLE identity + state fresco.
                                    Rendered in its OWN ReactiveJsonRoot (disjoint syncable
                                    taskSyncableHeader + own DataSync) so type/title/tags AND
                                    the workflow state stay editable on EVERY tab. The
                                    decoupled save is PARTIAL, so this header never clobbers
                                    the Informations-tab fields. Disabled (read-only) when
                                    the task is archived, in parity with the form card. */}
                                {attributes.id && (
                                    <div className={isArchived ? 'pointer-events-none opacity-60' : ''}>
                                        <ReactiveJsonRoot
                                            key={`header-${attributes.id}`}
                                            rjBuildUrl="/components/task/TaskIdentityHeader.yaml"
                                            dataOverride={infoPanelData}
                                            plugins={headerPlugins}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Main Form Card */}
                            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8 mb-6 border border-slate-200/75 relative z-0">

                                {/* Tabs & Dates */}
                                <div className="flex items-end justify-between gap-3 border-b border-slate-200 mb-6 relative z-0">
                                    <div className="flex gap-2 sm:gap-4 overflow-x-auto scrollbar-none -mx-1 px-1">
                                        <button
                                            onClick={() => setActiveMainTab('infos')}
                                            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                                                activeMainTab === 'infos'
                                                    ? 'border-iris-500 text-iris-600'
                                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                            }`}
                                        >
                                            Informations
                                        </button>
                                        <button
                                            onClick={() => setActiveMainTab('time')}
                                            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                                                activeMainTab === 'time'
                                                    ? 'border-iris-500 text-iris-600'
                                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                            }`}
                                        >
                                            Suivi des temps
                                        </button>
                                        <button
                                            onClick={() => setActiveMainTab('subtasks')}
                                            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                                                activeMainTab === 'subtasks'
                                                    ? 'border-iris-500 text-iris-600'
                                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                            }`}
                                        >
                                            Sous-tâches
                                            {subTasks && subTasks.length > 0 && (
                                                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-iris-100 text-iris-700 text-xs">
                                                    {subTasks.filter(t => t.data?.columnId === 103 || t.columnId === 103).length}/{subTasks.length}
                                                </span>
                                            )}
                                        </button>
                                    </div>

                                    <div className="text-xs text-slate-500 font-medium pb-2 hidden sm:flex items-center gap-4 shrink-0">
                                        <span>
                                            {attributes?.author ? `créé par ${attributes.author} le ` : 'créé le '}{formatHistoryDate(attributes.createdAt)} · mis à jour le {formatHistoryDate(attributes.updatedAt)}
                                        </span>
                                    </div>
                                </div>

                                {/* Informations tab — rendered on a gray canvas (bled to the
                                    form-card edges) so the white section cards stand out
                                    instead of being white-on-white. */}
                                {activeMainTab === 'infos' && (
                                    <div className={`-mx-4 sm:-mx-6 lg:-mx-8 -mb-4 sm:-mb-6 lg:-mb-8 px-4 sm:px-6 lg:px-8 py-5 sm:py-6 bg-slate-50 rounded-b-xl ${archivedPanelClass}`}>
                                        <ReactiveJsonRoot
                                            key={attributes?.id || 'loading'}
                                            rjBuildUrl="/components/task/TaskInfoPanel.yaml"
                                            dataOverride={infoPanelData}
                                            plugins={infoPanelPlugins}
                                        />
                                    </div>
                                )}

                                {/* Sub-tasks tab */}
                                {activeMainTab === 'subtasks' && (
                                    <div className={`flex flex-col h-full ${archivedPanelClass}`}>
                                        {subTasks.length > 0 && subtaskTypeOptions.length > 0 && (
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-iris-400">
                                                        <path d="M16 3h5v5" /><path d="M8 3H3v5" /><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" /><path d="m15 9 6-6" />
                                                    </svg>
                                                    <span>{subTasks.length} sous-tâche{subTasks.length > 1 ? 's' : ''}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleCreateSubTask}
                                                    className="btn btn-primary"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                                    </svg>
                                                    Ajouter une sous-tâche
                                                </button>
                                            </div>
                                        )}

                                        {subTasks.length > 0 ? (
                                            <KanbanBoard
                                                props={{
                                                    taskTypes: subtaskTypeOptions,
                                                    tasks: subTasks,
                                                    isSubTaskView: true,
                                                    users,
                                                    tagOptions,
                                                    returnTypeOptions,
                                                    typeOptions,
                                                    // Parité recette 3 : relayer les props que le
                                                    // kanban principal (kanban2.yaml) fournit, pour
                                                    // restaurer le menu de transition (currentUserRoles),
                                                    // la résolution de statut au drag (statusOptions) et
                                                    // la modale de création (evolutionTypeOptions).
                                                    evolutionTypeOptions,
                                                    statusOptions,
                                                    currentUserRoles,
                                                    onTaskDeleted: () => refreshSubTasks(),
                                                }}
                                            />
                                        ) : (
                                            <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
                                                <div className="w-16 h-16 rounded-full bg-iris-50 flex items-center justify-center mb-4">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-iris-300">
                                                        <path d="M16 3h5v5" /><path d="M8 3H3v5" /><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" /><path d="m15 9 6-6" />
                                                    </svg>
                                                </div>
                                                <p className="text-slate-500 text-sm mb-1">Aucune sous-tâche pour le moment</p>
                                                <p className="text-slate-400 text-xs mb-6">Décomposez cette tâche en sous-tâches pour un meilleur suivi</p>
                                                {subtaskTypeOptions.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={handleCreateSubTask}
                                                        className="btn btn-primary"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                                        </svg>
                                                        Créer la première sous-tâche
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Time tracking tab */}
                                {activeMainTab === 'time' && timeLogData && (
                                    <div className={`mt-4 ${archivedPanelClass}`}>
                                        <ReactiveJsonRoot
                                            rjBuildUrl="/components/task/TimeLogManager.yaml"
                                            dataOverride={timeLogData}
                                            plugins={timeLogPlugins}
                                        />
                                    </div>
                                )}
                            </div>
                          </div>
                        </div>

                        {/* Activity overlay backdrop (below lg only) */}
                        {showActivity && (
                            <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setShowActivity(false)} />
                        )}

                        {/* Right panel (Activity) — static pane at lg+, slide-in overlay below lg */}
                        <div
                            role={showActivity ? 'dialog' : undefined}
                            aria-modal={showActivity ? true : undefined}
                            aria-label="Commentaires et historique"
                            className={`bg-white border-slate-200 flex flex-col z-40 lg:static lg:w-[340px] xl:w-[380px] lg:flex-shrink-0 lg:border-l lg:shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] fixed inset-y-0 right-0 w-full sm:w-[400px] max-w-[90vw] border-l shadow-2xl transform transition-transform duration-200 ${showActivity ? 'translate-x-0' : 'translate-x-full'} lg:translate-x-0`}
                        >
                            {/* Sidebar Tabs */}
                            <div className="flex border-b border-slate-200 flex-shrink-0 items-center">
                                <button onClick={() => setActiveRightTab('message')} className={rightTabClass(activeRightTab === 'message')}>Commentaires</button>
                                <button onClick={() => setActiveRightTab('activity')} className={rightTabClass(activeRightTab === 'activity')}>Historique</button>
                                <button onClick={() => setShowActivity(false)} className="lg:hidden ml-auto px-3 text-slate-400 hover:text-slate-600 shrink-0" title="Fermer">
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Top Comment Input — TipTap with @mention */}
                            {activeRightTab === 'message' && (
                                <div className="border-b border-slate-200 bg-white shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] z-30 relative">
                                    <EditorContent
                                        editor={commentEditor}
                                        className="prose prose-sm prose-stone max-w-none px-4 pt-3 pb-1 min-h-16 [&_.tiptap]:outline-none [&_.tiptap]:min-h-12"
                                    />
                                    <div className="flex justify-end px-3 pb-2">
                                        <button
                                            onClick={handleAddComment}
                                            className="p-2 rounded-full transition-all shadow-sm hover:shadow-md bg-iris-50 text-iris-600 hover:bg-iris-600 hover:text-white cursor-pointer"
                                        >
                                            <Send size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Content */}
                            <div className="flex-grow min-h-0 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
                                {activeRightTab === 'message' ? (
                                    <>
                                        {comments.length === 0 && (
                                            <div className="text-center text-slate-400 py-8 italic">
                                                Aucun commentaire pour le moment.
                                            </div>
                                        )}
                                        {comments.map((comment, idx) => {
                                            const isEditing = editingCommentId === (comment.id || idx);
                                            return (
                                                <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 hover:shadow-md transition-shadow group">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                                                            <div className="avatar avatar-sm">
                                                                {comment.author ? comment.author.charAt(0) : '?'}
                                                            </div>
                                                            {comment.author}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-slate-400 font-medium">{formatHistoryDate(comment.date)}</span>
                                                            {!isEditing && canManageComment(comment) && !isArchived && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleEditComment(comment, idx)}
                                                                        className="text-slate-400 hover:text-iris-600 transition-colors"
                                                                        title="Modifier"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteComment(comment)}
                                                                        className="text-slate-400 hover:text-red-600 transition-colors"
                                                                        title="Supprimer"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <polyline points="3 6 5 6 21 6" />
                                                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                                        </svg>
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isEditing ? (
                                                        <div className="mt-2">
                                                            <div className="border-2 border-slate-300 rounded-md focus-within:ring-2 focus-within:ring-iris-500 focus-within:border-iris-500 mb-2 overflow-hidden">
                                                                <EditorContent
                                                                    editor={editCommentEditor}
                                                                    className="prose prose-sm prose-stone max-w-none px-3 py-2 min-h-[60px] [&_.tiptap]:outline-none [&_.tiptap]:min-h-[44px] [&_.mention]:bg-iris-100 [&_.mention]:text-iris-700 [&_.mention]:rounded [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:font-medium"
                                                                />
                                                            </div>
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    onClick={handleCancelEdit}
                                                                    className="btn btn-ghost btn-sm"
                                                                >
                                                                    Annule
                                                                </button>
                                                                <button
                                                                    onClick={() => handleSaveComment(idx)}
                                                                    disabled={isArchived}
                                                                    className="btn btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    Sauvegarde
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none [&_.mention]:bg-iris-100 [&_.mention]:text-iris-700 [&_.mention]:rounded [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:font-medium"
                                                            dangerouslySetInnerHTML={{ __html: sanitizeCommentHtml(comment.rawHtml || comment.text) }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <>
                                        {history.length === 0 && (
                                            <div className="text-center text-slate-400 py-8 italic">
                                                Aucun historique pour le moment.
                                            </div>
                                        )}
                                        {history.map((item, idx) => (
                                            <div key={item.id || idx} className="flex gap-4 text-sm relative group">
                                                <div className="mt-1.5 relative z-10">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300 group-hover:bg-iris-600 transition-colors ring-4 ring-slate-50 group-hover:ring-iris-50"></div>
                                                </div>
                                                <div className="pb-4 border-l border-slate-200 -ml-[19px] pl-8 flex-1">
                                                    <div className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                                                        <div className="flex items-start gap-2 mb-1">
                                                            <div className="avatar avatar-xs flex-shrink-0 mt-0.5">
                                                                {item.user ? item.user.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <div>
                                                                <span className="font-bold text-slate-900">{item.user}</span>
                                                                <span className="text-slate-600"> {item.detail || item.action}</span>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="text-xs text-slate-400 mt-1.5 font-medium ml-7 cursor-default"
                                                            title={item.date}
                                                        >
                                                            {formatHistoryDate(item.date)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Activity FAB (below lg only) — opens the overlay.
                            `lg:hidden` sits on the SLOT: on the button it would
                            leave an empty slot, hence a phantom gap. */}
                        <CornerDockSlot order={DOCK_ORDER.taskActivity} className="lg:hidden">
                            <button
                                onClick={() => setShowActivity(true)}
                                className="flex items-center gap-2 pl-4 pr-5 py-3 bg-iris-600 hover:bg-iris-700 text-white rounded-full shadow-lg transition-colors"
                                title="Commentaires & historique"
                                aria-label="Ouvrir les commentaires et l'historique"
                            >
                                <MessageSquare size={18} />
                                {comments.length > 0 && (
                                    <span className="text-xs font-bold leading-none">{comments.length}</span>
                                )}
                            </button>
                        </CornerDockSlot>
                    </div>
                </div>
                <ConfirmTrashModal
                    isOpen={showArchiveConfirm}
                    onConfirm={handleArchiveTask}
                    onCancel={() => setShowArchiveConfirm(false)}
                    title={`Archiver la tâche « ${attributes.title || ''} » ?`}
                    description="Cette tâche sera archivée en lecture seule. Vous pourrez la désarchiver à tout moment."
                    confirmLabel="Archiver"
                    loading={archiveLoading}
                    variant="archive"
                />
            </>
        </ActionDependant>
    );
};

export default TaskEdit;
